//// Application Packages
import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import { object, string } from 'yup'
import { translateMultiFormField } from '../../functions/field-functions'
import {
    subtractValuesAdditionalArrayForm,
    subtractValuesAdditionalConditionalForm,
    extractSubmissionForm,
} from '../../functions/form-functions'

//// Application Slices
import { selectSettings } from '../../reducers/settingSlice'
import {
    updateFormData,
    updateFormValidity,
    updateFormAuxiliary,
    deleteFormData,
    updateFormSystem,
    selectForms,
} from '../../reducers/formSlice'

//// Application Components
import ButtonComponent from '../../components/conditional-components/button-component'
import LoadingComponent from '../../components/simple-components/loading-component'
import NotificationComponent from '../../components/conditional-components/notification-component'
import AddFormComponent from '../../components/navigation-components/add-form-component'
import StateSelectorComponent from '../../components/conditional-components/state-selector-component'
import CountrySelectorComponent from '../../components/conditional-components/country-selector-component'
import AdditionalExaminationHistoryComponent from '../../components/licensed-practical-nursing-by-endorsement-components/additional-examination-history-component'

//// Carbon Components
import {
    Form,
    RadioButtonGroup,
    RadioButton,
    Select,
    SelectItem,
    DatePicker,
    DatePickerInput,
} from '@carbon/react'

//// Carbon Icons

//// Static Constants

//////// Component
const LicensedPracticalNursingExaminationExaminationHistoryForm = ({
    instance,
}) => {
    //////// Hooks
    const dispatch = useDispatch()
    const navigate = useNavigate()

    //////// State
    const [loading, setLoading] = useState(false)
    const [notification, setNotification] = useState({})

    //////// Slices
    const settingsData = useSelector(selectSettings)
    const formsData = useSelector(selectForms)

    //// Slice Data
    const digital_name = settingsData?.['form']?.['digital_name']
    const submit_route = settingsData?.['form']?.['submit_route']
    const fields = settingsData?.['form']?.['fields']
    const max_form_iterations =
        settingsData?.['application']?.['max_form_iterations']
    const conditions = settingsData?.['form']?.['conditions']

    //////// Search Params

    //////// Methods

    // Generate Validation Schema
    const generateSchema = async (auxAddtForms) => {
        try {
            const dynamic = {}
            if (auxAddtForms?.length > 0) {
                dynamic.addtObjs = [
                    {
                        EXAM: '',
                        STATE: '',
                        COUNTRY: '',
                        EXMDATE: '',
                        RESULT: '',
                    },
                ]
                dynamic.addtFilds = [
                    {
                        EXAM: false,
                        EXAMErrMsg: '',
                        STATE: false,
                        STATEErrMsg: '',
                        COUNTRY: false,
                        COUNTRYErrMsg: '',
                        EXMDATE: false,
                        EXMDATEErrMsg: '',
                        RESULT: false,
                        RESULTErrMsg: '',
                    },
                ]
                dynamic.addtObjs[0] = {
                    ...dynamic.addtObjs[0],
                    TKE_EXM: '',
                    EXAM: '',
                    STATE: '',
                    COUNTRY: '',
                    EXMDATE: '',
                    RESULT: '',
                }
                dynamic.addtFilds[0] = {
                    ...dynamic.addtFilds[0],
                    TKE_EXM: false,
                    TKE_EXMErrMsg: '',
                    EXAM: false,
                    EXAMErrMsg: '',
                    STATE: false,
                    STATEErrMsg: '',
                    COUNTRY: false,
                    COUNTRYErrMsg: '',
                    EXMDATE: false,
                    EXMDATEErrMsg: '',
                    RESULT: false,
                    RESULTErrMsg: '',
                }
                await auxAddtForms.forEach(async (el) => {
                    dynamic.addtObjs.push({
                        [`EXAM` + el]: '',
                        [`STATE` + el]: '',
                        [`COUNTRY` + el]: '',
                        [`EXMDATE` + el]: '',
                        [`RESULT` + el]: '',
                    })
                    dynamic.addtFilds.push({
                        [`EXAM` + el]: '',
                        [`EXAM` + el + 'ErrMsg']: false,
                        [`STATE` + el]: '',
                        [`STATE` + el + 'ErrMsg']: false,
                        [`COUNTRY` + el]: '',
                        [`COUNTRY` + el + 'ErrMsg']: false,
                        [`EXMDATE` + el]: '',
                        [`EXMDATE` + el + 'ErrMsg']: false,
                        [`RESULT` + el]: '',
                        [`RESULT` + el + 'ErrMsg']: false,
                    })
                })
                dynamic.addtObjsSchema = {}
                if (dynamic?.addtObjs?.length > 0) {
                    dynamic.addtObjs.forEach(async (el) => {
                        dynamic.addtObjsSchema = {
                            ...dynamic.addtObjsSchema,
                            ...el,
                        }
                    })
                }
                dynamic.addtFildsSchema = {}
                if (dynamic?.addtFilds?.length > 0) {
                    dynamic.addtFilds.forEach(async (el) => {
                        dynamic.addtFildsSchema = {
                            ...dynamic.addtFildsSchema,
                            ...el,
                        }
                    })
                }
                dynamic.addtObjsSchemaKeys = Object?.keys(
                    dynamic.addtObjsSchema
                )
                dynamic.addtFildsSchemaKeys = Object?.keys(
                    dynamic.addtFildsSchema
                )
                dynamic.addtSchema = {}
                if (dynamic?.addtObjsSchemaKeys?.length > 0) {
                    dynamic.addtObjsSchemaKeys.forEach(async (el) => {
                        dynamic.addtSchema = {
                            ...dynamic.addtSchema,
                            [el]: string().required(
                                'This field is required to be entered or selected.'
                            ),
                        }
                    })
                }
                dynamic.addtValid = {}
                if (dynamic?.addtFildsSchemaKeys?.length > 0) {
                    dynamic.addtFildsSchemaKeys.forEach(async (el) => {
                        if (el?.includes('ErrMsg')) {
                            dynamic.addtValid = {
                                ...dynamic.addtValid,
                                [el]: '',
                            }
                        } else {
                            dynamic.addtValid = {
                                ...dynamic.addtValid,
                                [el]: false,
                            }
                        }
                    })
                }
            }
            return dynamic
        } catch (error) {
            console.warn('Generate Schema Error:', error)
            return null
        }
    }

    // Validate Data Submission
    const validation = async (submit) => {
        // Validate Form Submission Data Against Schema
        try {
            // Schema Object
            const schema = {}
            if (
                formsData?.[digital_name]?.values?.TKE_EXM === undefined ||
                formsData?.[digital_name]?.values?.TKE_EXM === 'N'
            ) {
                schema.yup = object({
                    TKE_EXM: string().required(
                        'This field is required to be entered or selected.'
                    ),
                })
                schema.valid = {
                    TKE_EXM: false,
                    TKE_EXMErrMsg: '',
                }
            } else if (
                formsData?.[digital_name]?.values?.TKE_EXM === 'Y' &&
                formsData[digital_name].auxiliary.addtForms?.length === 0
            ) {
                schema.yup = object({
                    TKE_EXM: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    EXAM: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    STATE: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    COUNTRY: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    EXMDATE: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    RESULT: string().required(
                        'This field is required to be entered or selected.'
                    ),
                })
                schema.valid = {
                    TKE_EXM: false,
                    TKE_EXMErrMsg: '',
                    EXAM: false,
                    EXAMErrMsg: '',
                    STATE: false,
                    STATEErrMsg: '',
                    COUNTRY: false,
                    COUNTRYErrMsg: '',
                    EXMDATE: false,
                    EXMDATEErrMsg: '',
                    RESULT: false,
                    RESULTErrMsg: '',
                }
            } else if (
                formsData?.[digital_name]?.values?.TKE_EXM === 'Y' &&
                formsData[digital_name].auxiliary.addtForms?.length > 0
            ) {
                const addtForms = formsData[digital_name].auxiliary.addtForms
                const { addtSchema, addtValid } = await generateSchema(
                    addtForms
                )
                schema.yup = object().shape({ ...addtSchema })
                schema.valid = addtValid
            }

            await schema.yup.validate(
                { ...submit },
                { strict: true, abortEarly: false }
            )
            dispatch(
                updateFormValidity({
                    digital_name: digital_name,
                    errors: {},
                })
            )
            return true
        } catch (err) {
            const errors = {}
            if (err?.inner?.length > 0) {
                await err.inner.forEach(async (el) => {
                    if (el?.path) {
                        errors[el?.path] = true
                        if (el?.errors?.length > 0) {
                            const errName = el?.path + 'ErrMsg'
                            const errMsg = el?.errors?.join(' ')
                            const tran = translateMultiFormField(
                                fields,
                                max_form_iterations,
                                el?.path
                            )
                            if (tran) {
                                const tranErrMsg = await errMsg.replaceAll(
                                    el?.path,
                                    tran
                                )
                                errors[errName] = tranErrMsg
                            } else {
                                errors[errName] = el?.errors?.join(' ')
                            }
                        }
                    }
                })
            }
            //// Start Watson Assistant Journey

            //// End Watson Assistant Journey
            dispatch(
                updateFormValidity({
                    digital_name: digital_name,
                    errors: errors,
                })
            )
            return false
        }
    }

    // Re-Validate Data Submission
    const revalidation = async (event_name) => {
        try {
            // Update Validation State
            if (digital_name && event_name) {
                dispatch(
                    updateFormValidity({
                        digital_name: digital_name,
                        errors: {
                            [event_name]: false,
                            [event_name + 'ErrMsg']: '',
                        },
                    })
                )
            }
        } catch (error) {
            console.warn('Revalidation Error:', error)
            return null
        }
    }

    //////// Functional Methods

    // Handle Removal Of Additional Examination History Component/s
    const handleSubtractAdditionalExaminationHistoryComponent = async (e) => {
        try {
            if (e?.target?.name) {
                const addtForms = [
                    ...formsData[digital_name].auxiliary.addtForms,
                ]
                const subt = parseInt(e.target.name)
                const index = addtForms?.indexOf(subt)
                if (index > -1) {
                    addtForms.splice(index, 1)
                    dispatch(
                        updateFormAuxiliary({
                            digital_name: digital_name,
                            auxiliary: {
                                addtForms: addtForms,
                            },
                        })
                    )
                    const values = subtractValuesAdditionalArrayForm(
                        fields,
                        max_form_iterations,
                        formsData?.[digital_name]?.values,
                        index
                    )
                    if (values) {
                        dispatch(
                            deleteFormData({
                                digital_name: digital_name,
                            })
                        )
                        dispatch(
                            updateFormData({
                                digital_name: digital_name,
                                values: values,
                            })
                        )
                    }
                }
            }
        } catch (error) {
            console.warn(
                'Handle Subtract Additional Examination History Component Error:',
                error
            )
            return null
        }
    }

    // Handle Removal Of Additional Examination History Component/s
    const handleSubtractAdditionalConditionalComponent = (e) => {
        try {
            if (e?.target?.name && e?.target?.value) {
                const values = subtractValuesAdditionalConditionalForm(
                    conditions,
                    formsData?.[digital_name]?.values,
                    e?.target?.name,
                    e?.target?.value
                )
                if (values) {
                    dispatch(
                        deleteFormData({
                            digital_name: digital_name,
                        })
                    )
                    dispatch(
                        updateFormAuxiliary({
                            digital_name: digital_name,
                            auxiliary: {
                                addtForms: [],
                            },
                        })
                    )
                    dispatch(
                        updateFormData({
                            digital_name: digital_name,
                            values: values,
                        })
                    )
                }
            }
        } catch (error) {
            console.warn(
                'Handle Subtract Additional Examination History Component Error:',
                error
            )
            return null
        }
    }

    // Handle Data Of All Fields + DatePicker (ID)
    const handleData = (e, id) => {
        if (e?.target?.name && e?.target?.value) {
            dispatch(
                updateFormData({
                    digital_name: digital_name,
                    values: {
                        [e.target.name]: e.target.value,
                    },
                })
            )
            revalidation(e.target.name)
        } else if (e?.target?.name) {
            dispatch(
                updateFormData({
                    digital_name: digital_name,
                    values: { [e.target.name]: '' },
                })
            )
            revalidation(e.target.name)
        } else if (e?.length > 0 && id) {
            dispatch(
                updateFormData({
                    digital_name: digital_name,
                    values: {
                        [id]: format(new Date(e?.[0]), 'MM/dd/yyyy'),
                    },
                })
            )
            revalidation(id)
        }
        //// Start Watson Assistant Journey

        //// End Watson Assistant Journey
    }

    // Handle Form Submission
    const handleSubmit = async () => {
        try {
            console.info('Examination History: Submitted')

            setLoading(true)

            // Extract Form Data From Redux
            const submit = await extractSubmissionForm(digital_name, formsData)

            // Validate Form Submission Data
            const valid = await validation(submit)

            // Check If Form Data Is Valid
            if (valid) {
                console.info('Examination History: Passed Valid')

                dispatch(
                    updateFormSystem({
                        digital_name: digital_name,
                        system: { valid: true },
                    })
                )
                setLoading(false)
                setNotification({
                    type: 'inline',
                    visible: false,
                    kind: '',
                    title: '',
                    subtitle: '',
                })

                // Navigate To Next Form
                navigate(submit_route ? submit_route : '/')
            } else {
                console.info('Examination History: Failed Valid')

                dispatch(
                    updateFormSystem({
                        digital_name: digital_name,
                        system: { valid: false },
                    })
                )
                setLoading(false)
                setNotification({
                    type: 'inline',
                    visible: true,
                    kind: 'error',
                    title: 'Form Errors:',
                    subtitle:
                        'There are errors on the form (such as missing required info). Please scroll up and update the fields with red messages.',
                })

                console.log(
                    'Examination History Failed Valid: There are errors on the form (such as missing required info). Please scroll up and update the fields with red messages.'
                )
            }
        } catch (error) {
            console.warn('Handle Submit Error:', error)
            return null
        }
    }

    // Handle Keyboard Enter As Submit (Method)
    const handleKeyDown = async (event) => {
        try {
            if (event?.key === 'Enter') {
                await handleSubmit()
            }
        } catch (error) {
            console.warn('Handle Down Key Error:', error)
            return null
        }
    }

    //////// Memoization

    //////// Effects/Side Effects

    // Reload Watson Assistant Journey on Reload
    useEffect(() => {
        if (instance?.tours) {
            instance.tours.endTour()
            instance.tours.startTour(
                'RN OR LPN Examination Application Exam History Page Render Tour'
            )
        }
        return () => {}
    }, [instance])

    //////////////// Render & Return
    if (loading) {
        return (
            <>
                <Form
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                    onSubmit={handleSubmit}
                >
                    <LoadingComponent />
                </Form>
            </>
        )
    } else if (
        formsData?.[digital_name]?.values?.TKE_EXM === undefined ||
        formsData?.[digital_name]?.values?.TKE_EXM === 'N'
    ) {
        return (
            <>
                <Form
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                    onSubmit={handleSubmit}
                >
                    <p>
                        <strong>
                            This information is exempt from public records
                            disclosure.
                        </strong>
                    </p>

                    <p>
                        <strong>
                            Failure to disclose information in this section may
                            result in denial of your application.
                        </strong>
                    </p>

                    <p>
                        All applicants applying for the NCLEX examination
                        through Florida for the first time are considered
                        initial applicants, regardless of whether you have
                        previously taken the examination in a different state.
                    </p>

                    <p>
                        If you are a first-time examination taker in Florida,
                        this is the correct application for you to complete. If
                        you have taken the examination for Florida previously
                        you will need to apply for re-examination. To locate the
                        re-examination application visit{' '}
                        <a
                            href="https://floridasnursing.gov/applications/LPN-RN-re-exam-app.pdf"
                            target="_blank"
                            rel="noreferrer noopener"
                            aria-label="nursing-licensure-compact-link"
                            type="link"
                        >
                            https://floridasnursing.gov/applications/LPN-RN-re-exam-app.pdf
                        </a>
                        .
                    </p>

                    <RadioButtonGroup
                        className={
                            formsData?.[digital_name]?.errors?.TKE_EXM
                                ? 'required invalid'
                                : 'required'
                        }
                        defaultSelected={
                            formsData?.[digital_name]?.values?.TKE_EXM
                        }
                        valueSelected={
                            formsData?.[digital_name]?.values?.TKE_EXM
                        }
                        legendText="Have you ever taken an examination for RN or LPN licensure?"
                        name="TKE_EXM"
                        invalid={formsData?.[digital_name]?.errors?.TKE_EXM}
                        invalidText={
                            formsData?.[digital_name]?.errors?.TKE_EXMErrMsg
                        }
                    >
                        <RadioButton
                            id="TKE_EXMYes"
                            name="TKE_EXMYes"
                            labelText="Yes"
                            value="Y"
                            type="radio"
                            onClick={(e) => {
                                handleData(e)
                                handleSubtractAdditionalConditionalComponent(e)
                            }}
                        />
                        <RadioButton
                            id="TKE_EXMNo"
                            name="TKE_EXMNo"
                            labelText="No"
                            value="N"
                            type="radio"
                            onClick={(e) => {
                                handleData(e)
                                handleSubtractAdditionalConditionalComponent(e)
                            }}
                        />
                    </RadioButtonGroup>

                    <p>
                        <strong>If {'"Yes,"'}</strong> complete the following:
                        <br />
                        Several security measures will be enforced during the
                        administration of the examination. Strict candidate
                        identification requirements have been established by
                        NCSBN. Find out more at{' '}
                        <a
                            href="https://www.nclex.com/prepare.page"
                            target="_blank"
                            rel="noreferrer"
                        >
                            https://www.nclex.com/prepare.page
                        </a>
                        .
                    </p>

                    <p>
                        <strong>
                            Only the identification listed below will be
                            accepted:
                        </strong>
                        <br />
                        <ul>
                            <li>U.S. Driver License</li>
                            <li>
                                Provincial/Territorial or State Identification
                                Card
                            </li>
                            <li>
                                Passport (The only identification acceptable for
                                testing centers outside the U.S.)
                            </li>
                            <li>U.S. Military Identification</li>
                            <li>Permanent Residence Card</li>
                        </ul>
                    </p>

                    <p>
                        <strong>
                            All identification, including temporary
                            identification*, must:
                        </strong>
                        <br />
                        <ul>
                            <li>Be valid</li>
                            <li>Include a photograph</li>
                            <li>Not be expired</li>
                            <li>Contain your name in Roman characters</li>
                            <li>Include a signature</li>
                            <li>Be government-issued</li>
                        </ul>
                    </p>

                    <p>
                        * Examples of temporary identification include limited
                        term IDs and any ID reading {'"temp"'} or{' '}
                        {'"temporary."'}
                    </p>

                    <NotificationComponent notification={notification} />

                    <div className="button-group">
                        <div className="button-group-item">
                            <ButtonComponent
                                click={handleSubmit}
                                title="Save Progress"
                            />
                        </div>
                    </div>
                </Form>
            </>
        )
    } else if (formsData?.[digital_name]?.values?.TKE_EXM === 'Y') {
        return (
            <>
                <Form onSubmit={handleSubmit}>
                    <p>
                        <strong>
                            This information is exempt from public records
                            disclosure.
                        </strong>
                    </p>

                    <p>
                        <strong>
                            Failure to disclose information in this section may
                            result in denial of your application.
                        </strong>
                    </p>

                    <p>
                        All applicants applying for the NCLEX examination
                        through Florida for the first time are considered
                        initial applicants, regardless of whether you have
                        previously taken the examination in a different state.
                    </p>

                    <p>
                        If you are a first-time examination taker in Florida,
                        this is the correct application for you to complete. If
                        you have taken the examination for Florida previously
                        you will need to apply for re-examination. To locate the
                        re-examination application visit{' '}
                        <a
                            href="https://floridasnursing.gov/applications/LPN-RN-re-exam-app.pdf"
                            target="_blank"
                            rel="noreferrer noopener"
                            aria-label="nursing-licensure-compact-link"
                            type="link"
                        >
                            https://floridasnursing.gov/applications/LPN-RN-re-exam-app.pdf
                        </a>
                        .
                    </p>

                    <RadioButtonGroup
                        className={
                            formsData?.[digital_name]?.errors?.TKE_EXM
                                ? 'required invalid'
                                : 'required'
                        }
                        defaultSelected={
                            formsData?.[digital_name]?.values?.TKE_EXM
                        }
                        valueSelected={
                            formsData?.[digital_name]?.values?.TKE_EXM
                        }
                        legendText="Have you ever taken an examination for RN or LPN licensure?"
                        name="TKE_EXM"
                        invalid={formsData?.[digital_name]?.errors?.TKE_EXM}
                        invalidText={
                            formsData?.[digital_name]?.errors?.TKE_EXMErrMsg
                        }
                    >
                        <RadioButton
                            id="TKE_EXMYes"
                            name="TKE_EXMYes"
                            labelText="Yes"
                            value="Y"
                            type="radio"
                            onClick={(e) => {
                                handleData(e)
                                handleSubtractAdditionalConditionalComponent(e)
                            }}
                        />
                        <RadioButton
                            id="TKE_EXMNo"
                            name="TKE_EXMNo"
                            labelText="No"
                            value="N"
                            type="radio"
                            onClick={(e) => {
                                handleData(e)
                                handleSubtractAdditionalConditionalComponent(e)
                            }}
                        />
                    </RadioButtonGroup>

                    <Select
                        className="required"
                        defaultValue={formsData?.[digital_name]?.values?.EXAM}
                        value={formsData?.[digital_name]?.values?.EXAM}
                        helper=""
                        labelText="Examination:"
                        id="EXAM"
                        name="EXAM"
                        onChange={handleData}
                        invalid={formsData?.[digital_name]?.errors?.EXAM}
                        invalidText={
                            formsData?.[digital_name]?.errors?.EXAMErrMsg
                        }
                    >
                        <SelectItem text="Choose an option" value="" />
                        <SelectItem text="RN" value="RN" />
                        <SelectItem text="LPN" value="LPN" />
                    </Select>

                    <CountrySelectorComponent
                        digital_name={digital_name}
                        value_key="COUNTRY"
                        state_value_key="STATE"
                        county_value_key=""
                    />

                    <StateSelectorComponent
                        digital_name={digital_name}
                        value_key="STATE"
                        country={formsData?.[digital_name]?.values?.COUNTRY}
                    />

                    <DatePicker
                        dateFormat="m/d/Y"
                        datePickerType="single"
                        onChange={(e) => handleData(e, 'EXMDATE')}
                        onClose={(e) => handleData(e, 'EXMDATE')}
                        value={formsData?.[digital_name]?.values?.EXMDATE}
                    >
                        <DatePickerInput
                            className="required"
                            labelText="Examination Date:"
                            id="EXMDATE"
                            name="EXMDATE"
                            placeholder="MM/DD/YYYY"
                            type="text"
                            invalid={formsData?.[digital_name]?.errors?.EXMDATE}
                            invalidText={
                                formsData?.[digital_name]?.errors?.EXMDATEErrMsg
                            }
                        />
                    </DatePicker>

                    <Select
                        className="required"
                        defaultValue={formsData?.[digital_name]?.values?.RESULT}
                        value={formsData?.[digital_name]?.values?.RESULT}
                        helper=""
                        labelText="Result:"
                        id="RESULT"
                        name="RESULT"
                        onChange={handleData}
                        invalid={formsData?.[digital_name]?.errors?.RESULT}
                        invalidText={
                            formsData?.[digital_name]?.errors?.RESULTErrMsg
                        }
                    >
                        <SelectItem text="Choose an option" value="" />
                        <SelectItem text="Pass" value="P" />
                        <SelectItem text="Fail" value="F" />
                    </Select>

                    <p>
                        Those who have not taken and passed the NCLEX or SBTPE
                        are not eligible for multistate licensure and will be
                        issued a single state license upon approval of their
                        application.
                    </p>

                    {formsData[digital_name].auxiliary.addtForms.map((el) => (
                        <AdditionalExaminationHistoryComponent
                            key={el}
                            number={el}
                            handleData={handleData}
                            handleSubtractAdditionalExaminationHistoryComponent={
                                handleSubtractAdditionalExaminationHistoryComponent
                            }
                        />
                    ))}

                    <AddFormComponent digital_name={digital_name} />

                    <p>
                        <strong>If {'"Yes,"'}</strong> complete the following:
                        <br />
                        Several security measures will be enforced during the
                        administration of the examination. Strict candidate
                        identification requirements have been established by
                        NCSBN. Find out more at{' '}
                        <a
                            href="https://www.nclex.com/prepare.page"
                            target="_blank"
                            rel="noreferrer"
                        >
                            https://www.nclex.com/prepare.page
                        </a>
                        .
                    </p>

                    <p>
                        <strong>
                            Only the identification listed below will be
                            accepted:
                        </strong>
                        <br />
                        <ul>
                            <li>U.S. Driver License</li>
                            <li>
                                Provincial/Territorial or State Identification
                                Card
                            </li>
                            <li>
                                Passport (The only identification acceptable for
                                testing centers outside the U.S.)
                            </li>
                            <li>U.S. Military Identification</li>
                            <li>Permanent Residence Card</li>
                        </ul>
                    </p>

                    <p>
                        <strong>
                            All identification, including temporary
                            identification*, must:
                        </strong>
                        <br />
                        <ul>
                            <li>Be valid</li>
                            <li>Include a photograph</li>
                            <li>Not be expired</li>
                            <li>Contain your name in Roman characters</li>
                            <li>Include a signature</li>
                            <li>Be government-issued</li>
                        </ul>
                    </p>

                    <p>
                        * Examples of temporary identification include limited
                        term IDs and any ID reading {'"temp"'} or{' '}
                        {'"temporary."'}
                    </p>

                    <NotificationComponent notification={notification} />

                    <div className="button-group">
                        <div className="button-group-item">
                            <ButtonComponent
                                click={handleSubmit}
                                title="Save Progress"
                            />
                        </div>
                    </div>
                </Form>
            </>
        )
    }
}

export default LicensedPracticalNursingExaminationExaminationHistoryForm
